// ============================================================
// UserTimelineColoursPanel.tsx
// PER-USER override for the action-type timeline colours, shown in
// Settings → Appearance (every member, not just admins).
//
// These colours are PERSONAL: they override the firm's house colours for
// THIS user's Activity timeline only, and never touch the firm scheme or
// anyone else's view. They sit at the top of the three-layer cascade:
//     user.currentScheme  ◄  firm.scheme  ◄  shipped default
// so each action type here is seeded from the EFFECTIVE colour the user
// currently sees (their override, else the firm's, else the shipped
// default).
//
// Reuses the shared ActionColourAccordion (same picker the admin firm
// panel uses). Picking a swatch persists the user's currentScheme live
// (savedTemplate untouched) and recolours their own timeline immediately.
//
// Four template controls:
//   • Save as my template   → currentScheme = savedTemplate = selections
//   • Revert to firm colours → selections/currentScheme = firm cascade;
//                              savedTemplate untouched
//   • Restore my template    → selections/currentScheme = savedTemplate;
//                              savedTemplate untouched (enabled only when
//                              a template exists)
//   • Reset all              → currentScheme = {} AND savedTemplate = {};
//                              the cascade falls back to firm/shipped
// ============================================================
import React, { useEffect, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { ACTION_TYPES } from '../../constants/actionTypes.constants'
import { SHIPPED_DEFAULT_SCHEME } from '../../constants/palette.constants'
import { getUserColourSchemeApi, putUserColourSchemeApi } from '../../api/casevoApi'
import { ActionColourAccordion } from './ActionColourAccordion'
import { SettingsCard, SettingsSaved, S_BTN_GHOST_SM, S_BTN_PRIMARY_SM, S_FORMFOOT, S_NOTE } from '../settings/settingsUi'

type Status = 'saved' | 'template-saved' | 'reverted' | 'restored' | 'reset' | null

export const UserTimelineColoursPanel: React.FC = () => {
  const firmColourScheme = useCasevoStore((s) => s.firmColourScheme)
  const setUserColourScheme = useCasevoStore((s) => s.setUserColourScheme)

  // The EFFECTIVE firm colour per action type (firm override, else the
  // shipped default) — the target of "Revert to firm colours" and the
  // fallback for display after "Reset all". Recomputed from the store's
  // firm scheme so an admin change is reflected without a reload.
  const firmSelections = (): Record<string, string> => {
    const s: Record<string, string> = {}
    for (const t of ACTION_TYPES) s[t.value] = firmColourScheme[t.value] ?? SHIPPED_DEFAULT_SCHEME[t.value]
    return s
  }

  // What the accordion shows/edits — the user's effective selection per
  // type. Seeded to the firm cascade; the user's currentScheme merges
  // over it once loaded.
  const [selections, setSelections] = useState<Record<string, string>>(firmSelections)
  // The user's stored personal template ({} = none) — drives whether
  // "Restore my template" is enabled.
  const [savedTemplate, setSavedTemplate] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>(null)

  // Load the user's scheme on mount and seed selections from the
  // cascade: currentScheme[type] ?? firm[type] ?? shipped[type].
  useEffect(() => {
    let cancelled = false
    getUserColourSchemeApi()
      .then(({ currentScheme, savedTemplate: tpl }) => {
        if (cancelled) return
        const firm = firmSelections()
        const next: Record<string, string> = {}
        for (const t of ACTION_TYPES) next[t.value] = currentScheme[t.value] ?? firm[t.value]
        setSelections(next)
        setSavedTemplate(tpl)
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load colours') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // firmColourScheme intentionally omitted — we seed once on mount; the
    // store update from any control keeps rendering in sync separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Confirmation auto-fade (2s), matching the admin panels.
  useEffect(() => {
    if (!status) return
    const timer = setTimeout(() => setStatus(null), 2000)
    return () => clearTimeout(timer)
  }, [status])

  // Persist currentScheme (+ optionally savedTemplate) and mirror the
  // result into the store so the user's OWN timeline recolours at once.
  // savedTemplate omitted (undefined) leaves the stored template as-is.
  const persist = async (
    currentScheme: Record<string, string>,
    template: Record<string, string> | undefined,
    done: Status,
  ): Promise<void> => {
    setError(null)
    setBusy(true)
    try {
      const res = await putUserColourSchemeApi(currentScheme, template)
      setUserColourScheme(res.currentScheme) // firm scheme untouched
      setSavedTemplate(res.savedTemplate)
      setStatus(done)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save colours')
    } finally {
      setBusy(false)
    }
  }

  // Pick a swatch → update selection + live preview, and persist it as
  // the user's currentScheme (their live override). Template untouched.
  const pick = (actionType: string, token: string): void => {
    const next = { ...selections, [actionType]: token }
    setSelections(next)
    void persist(next, undefined, 'saved')
  }

  // Save the current selections as the user's personal template AND their
  // live scheme.
  const saveAsTemplate = (): void => {
    void persist(selections, selections, 'template-saved')
  }
  // Drop back to the firm's colours (snapshot into currentScheme);
  // template preserved.
  const revertToFirm = (): void => {
    const next = firmSelections()
    setSelections(next)
    void persist(next, undefined, 'reverted')
  }
  // Re-apply the saved template as the live scheme; template preserved.
  const restoreTemplate = (): void => {
    if (Object.keys(savedTemplate).length === 0) return
    const next = { ...savedTemplate }
    setSelections(next)
    void persist(next, undefined, 'restored')
  }
  // Clear BOTH the live scheme and the template — the cascade falls back
  // to the firm/shipped colours.
  const resetAll = (): void => {
    setSelections(firmSelections())
    void persist({}, {}, 'reset')
  }

  const templateExists = Object.keys(savedTemplate).length > 0
  const statusText: Record<Exclude<Status, null>, string> = {
    'saved': 'Saved ✓',
    'template-saved': 'Template saved ✓',
    'reverted': 'Reverted to firm colours ✓',
    'restored': 'Template restored ✓',
    'reset': 'Reset ✓',
  }

  return (
    <SettingsCard
      icon="palette"
      title="Timeline colours"
      sub="Your own colours for activity types on the client timeline — only you see them."
      flushBody
      right={
        <button type="button" onClick={revertToFirm} disabled={busy || loading} className={S_BTN_GHOST_SM}>
          Revert to firm colours
        </button>
      }
    >
      {loading ? (
        <p className={`${S_NOTE} pt-[12px]`}>Loading colours…</p>
      ) : (
        <>
          <div className="pt-[12px]">
            <ActionColourAccordion selections={selections} onPick={pick} />
          </div>
          {error && <p className="text-[12.5px] text-casevo-danger mt-[12px]" role="alert">{error}</p>}
          {/* Template controls. Save/Restore manage your personal
              template; Revert (header) / Reset step back toward the
              firm's colours. */}
          <div className={S_FORMFOOT}>
            {status
              ? <SettingsSaved>{statusText[status].replace(' ✓', '')}</SettingsSaved>
              : <span className={S_NOTE}>Revert keeps your saved template. Reset all clears both your colours and your template.</span>}
            <span className="flex-1" />
            <button type="button" onClick={resetAll} disabled={busy} className={S_BTN_GHOST_SM}>
              Reset all
            </button>
            <button
              type="button"
              onClick={restoreTemplate}
              disabled={busy || !templateExists}
              title={templateExists ? undefined : 'Save a template first'}
              className={S_BTN_GHOST_SM}
            >
              Restore my template
            </button>
            <button type="button" onClick={saveAsTemplate} disabled={busy} className={S_BTN_PRIMARY_SM}>
              Save as my template
            </button>
          </div>
        </>
      )}
    </SettingsCard>
  )
}
